/**
 * MEVIA v5.0 - Employee Expediente Component
 * Full employee profile with 6 tabs: Personal, Domicilio, Laboral, Documentos, Evaluaciones, Planes de Acción
 * Role-based permissions for editing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  User, FileText, Briefcase, MapPin, Phone, Mail,
  Calendar, Save, X, Upload, Download, AlertCircle, Camera,
  ClipboardList, Target, TrendingUp, Building2, Clock, Award,
  ChevronRight, History, DollarSign, UserCheck, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Expediente de Empleado',
    personal: 'Personal',
    address: 'Domicilio',
    job: 'Laboral',
    documents: 'Documentos',
    evaluations: 'Evaluaciones',
    actionPlans: 'Planes de Acción',
    
    // Personal fields
    firstName: 'Nombre(s)',
    lastName: 'Apellido Paterno',
    motherLastName: 'Apellido Materno',
    birthDate: 'Fecha de Nacimiento',
    gender: 'Género',
    nationality: 'Nacionalidad',
    curp: 'CURP',
    rfc: 'RFC',
    nss: 'NSS',
    phone: 'Teléfono',
    email: 'Correo Electrónico',
    profilePhoto: 'Foto de Perfil',
    
    // Address fields
    street: 'Calle',
    extNumber: 'No. Exterior',
    intNumber: 'No. Interior',
    neighborhood: 'Colonia',
    zipCode: 'Código Postal',
    city: 'Ciudad',
    state: 'Estado',
    country: 'País',
    
    // Emergency contact
    emergencyContact: 'Contacto de Emergencia',
    contactName: 'Nombre del Contacto',
    relationship: 'Parentesco',
    contactPhone: 'Teléfono de Contacto',
    
    // Job fields
    employeeNumber: 'Número de Empleado',
    hireDate: 'Fecha de Ingreso',
    terminationDate: 'Fecha de Baja',
    contractType: 'Tipo de Contrato',
    position: 'Puesto',
    area: 'Área',
    directBoss: 'Jefe Directo',
    salary: 'Salario',
    employeeStatus: 'Estado del Empleado',
    branch: 'Sucursal',
    level: 'Nivel',
    
    // Status options
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    statusOnLeave: 'De Permiso',
    
    // Gender options
    genderM: 'Masculino',
    genderF: 'Femenino',
    genderNB: 'No Binario',
    genderNA: 'Prefiero no decir',
    
    // Documents
    uploadDocument: 'Subir Documento',
    documentType: 'Tipo de Documento',
    expirationDate: 'Fecha de Vencimiento',
    uploadedBy: 'Subido por',
    uploadDate: 'Fecha de Carga',
    noDocuments: 'No hay documentos registrados',
    expiresOn: 'Vence',
    expired: 'Vencido',
    
    // Evaluations
    noEvaluations: 'No hay evaluaciones completadas',
    evaluationDate: 'Fecha de Evaluación',
    score: 'Puntaje',
    viewResults: 'Ver Resultados',
    
    // Action Plans
    noPlans: 'No hay planes de acción asignados',
    planStatus: 'Estado del Plan',
    dueDate: 'Fecha Límite',
    progress: 'Progreso',
    
    // Actions
    save: 'Guardar',
    cancel: 'Cancelar',
    edit: 'Editar',
    upload: 'Subir',
    download: 'Descargar',
    changePhoto: 'Cambiar Foto',
    assignEvaluation: 'Asignar Evaluación',
    viewReports: 'Ver Reportes',
    
    // Messages
    saved: 'Guardado exitosamente',
    error: 'Error al procesar',
    uploadSuccess: 'Documento subido exitosamente',
    photoUpdated: 'Foto actualizada',
    nameRequired: 'Nombre y Apellido Paterno son requeridos',
    employeeNumberRequired: 'Número de empleado es requerido',
    hireDateRequired: 'Fecha de ingreso es requerida',
    
    // Panel
    meviaScore: 'MEVIA Score',
    noScore: 'Sin evaluación',
  },
  en: {
    title: 'Employee Profile',
    personal: 'Personal',
    address: 'Address',
    job: 'Job Info',
    documents: 'Documents',
    evaluations: 'Evaluations',
    actionPlans: 'Action Plans',
    
    // Personal fields
    firstName: 'First Name(s)',
    lastName: 'Last Name',
    motherLastName: "Mother's Last Name",
    birthDate: 'Birth Date',
    gender: 'Gender',
    nationality: 'Nationality',
    curp: 'CURP',
    rfc: 'RFC',
    nss: 'SSN',
    phone: 'Phone',
    email: 'Email',
    profilePhoto: 'Profile Photo',
    
    // Address fields
    street: 'Street',
    extNumber: 'Ext. Number',
    intNumber: 'Int. Number',
    neighborhood: 'Neighborhood',
    zipCode: 'Zip Code',
    city: 'City',
    state: 'State',
    country: 'Country',
    
    // Emergency contact
    emergencyContact: 'Emergency Contact',
    contactName: 'Contact Name',
    relationship: 'Relationship',
    contactPhone: 'Contact Phone',
    
    // Job fields
    employeeNumber: 'Employee Number',
    hireDate: 'Hire Date',
    terminationDate: 'Termination Date',
    contractType: 'Contract Type',
    position: 'Position',
    area: 'Area',
    directBoss: 'Direct Manager',
    salary: 'Salary',
    employeeStatus: 'Employee Status',
    branch: 'Branch',
    level: 'Level',
    
    // Status options
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusOnLeave: 'On Leave',
    
    // Gender options
    genderM: 'Male',
    genderF: 'Female',
    genderNB: 'Non-Binary',
    genderNA: 'Prefer not to say',
    
    // Documents
    uploadDocument: 'Upload Document',
    documentType: 'Document Type',
    expirationDate: 'Expiration Date',
    uploadedBy: 'Uploaded by',
    uploadDate: 'Upload Date',
    noDocuments: 'No documents registered',
    expiresOn: 'Expires',
    expired: 'Expired',
    
    // Evaluations
    noEvaluations: 'No completed evaluations',
    evaluationDate: 'Evaluation Date',
    score: 'Score',
    viewResults: 'View Results',
    
    // Action Plans
    noPlans: 'No action plans assigned',
    planStatus: 'Plan Status',
    dueDate: 'Due Date',
    progress: 'Progress',
    
    // Actions
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    upload: 'Upload',
    download: 'Download',
    changePhoto: 'Change Photo',
    assignEvaluation: 'Assign Evaluation',
    viewReports: 'View Reports',
    
    // Messages
    saved: 'Saved successfully',
    error: 'Error processing',
    uploadSuccess: 'Document uploaded successfully',
    photoUpdated: 'Photo updated',
    nameRequired: 'First Name and Last Name are required',
    employeeNumberRequired: 'Employee number is required',
    hireDateRequired: 'Hire date is required',
    
    // Panel
    meviaScore: 'MEVIA Score',
    noScore: 'No evaluation',
  }
};

const GENDER_OPTIONS = [
  { value: 'M', labelEs: 'Masculino', labelEn: 'Male' },
  { value: 'F', labelEs: 'Femenino', labelEn: 'Female' },
  { value: 'NB', labelEs: 'No Binario', labelEn: 'Non-Binary' },
  { value: 'NA', labelEs: 'Prefiero no decir', labelEn: 'Prefer not to say' },
];

const STATUS_OPTIONS = [
  { value: 'activo', labelEs: 'Activo', labelEn: 'Active', color: 'bg-green-500' },
  { value: 'inactivo', labelEs: 'Inactivo', labelEn: 'Inactive', color: 'bg-gray-500' },
  { value: 'permiso', labelEs: 'De Permiso', labelEn: 'On Leave', color: 'bg-yellow-500' },
];

const DOCUMENT_TYPES = [
  { value: 'identificacion', labelEs: 'Identificación Oficial', labelEn: 'Official ID', hasExpiration: false },
  { value: 'comprobante_domicilio', labelEs: 'Comprobante de Domicilio', labelEn: 'Proof of Address', hasExpiration: true },
  { value: 'acta_nacimiento', labelEs: 'Acta de Nacimiento', labelEn: 'Birth Certificate', hasExpiration: false },
  { value: 'curp', labelEs: 'CURP', labelEn: 'CURP', hasExpiration: false },
  { value: 'rfc', labelEs: 'RFC', labelEn: 'RFC', hasExpiration: false },
  { value: 'contrato', labelEs: 'Contrato Laboral', labelEn: 'Employment Contract', hasExpiration: false },
  { value: 'estudios', labelEs: 'Comprobante de Estudios', labelEn: 'Education Certificate', hasExpiration: false },
  { value: 'certificaciones', labelEs: 'Certificaciones', labelEn: 'Certifications', hasExpiration: true },
  { value: 'examen_medico', labelEs: 'Examen Médico', labelEn: 'Medical Exam', hasExpiration: true },
  { value: 'foto', labelEs: 'Fotografía', labelEn: 'Photo', hasExpiration: false },
  { value: 'otro', labelEs: 'Otro', labelEn: 'Other', hasExpiration: false },
];

const EmployeeExpediente = ({ 
  employeeId, 
  isOpen, 
  onClose, 
  onUpdate,
  mode = 'full' // 'full' | 'self' - self is for Mi MEVIA
}) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  const empresaId = user?.empresa_id;
  const userRole = user?.rol || user?.role || 'employee';
  
  // Determine if current user is viewing their own profile
  const isOwnProfile = user?.id === employeeId;
  
  // Permission checks based on role
  const canEditPersonal = userRole === 'super_admin' || userRole === 'admin' || userRole === 'company_admin' || isOwnProfile;
  const canEditLaboral = userRole === 'super_admin' || userRole === 'admin' || userRole === 'company_admin';
  const canViewSalary = userRole === 'super_admin' || userRole === 'admin' || userRole === 'company_admin';
  const canEditPhoto = userRole === 'super_admin' || userRole === 'admin' || userRole === 'company_admin' || isOwnProfile;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [actionPlans, setActionPlans] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({});
  const originalFormDataRef = useRef({});
  
  // Organization data
  const [areas, setAreas] = useState([]);
  const [levels, setLevels] = useState([]);
  const [positions, setPositions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  
  // Document upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    tipo: '',
    fecha_expiracion: '',
    notas: ''
  });

  useEffect(() => {
    if (isOpen && employeeId) {
      loadAllData();
    }
  }, [isOpen, employeeId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadProfile(),
        loadDocuments(),
        loadEvaluations(),
        loadActionPlans(),
        loadOrganizationData(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/employees/${employeeId}/profile`);
      if (response.data.success) {
        const data = response.data.data;
        setProfile(data);
        
        // Parse JSON fields if they're strings
        let direccion = data.direccion || {};
        let contacto = data.contacto_emergencia || {};
        
        if (typeof direccion === 'string') {
          try { direccion = JSON.parse(direccion); } catch { direccion = {}; }
        }
        if (typeof contacto === 'string') {
          try { contacto = JSON.parse(contacto); } catch { contacto = {}; }
        }
        
        setFormData({
          // Personal
          nombre: data.nombre || '',
          apellido_paterno: data.apellido_paterno || '',
          apellido_materno: data.apellido_materno || '',
          fecha_nacimiento: data.fecha_nacimiento?.split('T')[0] || '',
          genero: data.genero || '',
          nacionalidad: data.nacionalidad || '',
          curp: data.curp || '',
          rfc: data.rfc || '',
          nss: data.nss || '',
          telefono: data.telefono || '',
          email: data.email || '',
          foto_url: data.foto_url || '',
          
          // Address
          direccion: direccion,
          
          // Emergency Contact
          contacto_emergencia: contacto,
          
          // Job
          numero_empleado: data.numero_empleado || '',
          fecha_ingreso: data.fecha_ingreso?.split('T')[0] || '',
          fecha_baja: data.fecha_baja?.split('T')[0] || '',
          tipo_contrato_id: data.tipo_contrato_id || '',
          puesto_id: data.puesto_id || '',
          area_id: data.area_id || '',
          nivel_id: data.nivel_id || '',
          sucursal_id: data.sucursal_id || '',
          jefe_directo_id: data.jefe_directo_id || '',
          salario: data.salario || '',
          estado_empleado: data.estado_empleado || 'activo',
        });
        // Store a snapshot for diffing on save
        originalFormDataRef.current = {
          nombre: data.nombre || '',
          apellido_paterno: data.apellido_paterno || '',
          apellido_materno: data.apellido_materno || '',
          fecha_nacimiento: data.fecha_nacimiento?.split('T')[0] || '',
          genero: data.genero || '',
          nacionalidad: data.nacionalidad || '',
          curp: data.curp || '',
          rfc: data.rfc || '',
          nss: data.nss || '',
          telefono: data.telefono || '',
          email: data.email || '',
          foto_url: data.foto_url || '',
          direccion: direccion,
          contacto_emergencia: contacto,
          numero_empleado: data.numero_empleado || '',
          fecha_ingreso: data.fecha_ingreso?.split('T')[0] || '',
          fecha_baja: data.fecha_baja?.split('T')[0] || '',
          tipo_contrato_id: data.tipo_contrato_id || '',
          puesto_id: data.puesto_id || '',
          area_id: data.area_id || '',
          nivel_id: data.nivel_id || '',
          sucursal_id: data.sucursal_id || '',
          jefe_directo_id: data.jefe_directo_id || '',
          salario: data.salario || '',
          estado_empleado: data.estado_empleado || 'activo',
        };
      }
    } catch (error) {
      logger.error('Error loading profile:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/employees/${employeeId}/documents`);
      if (response.data.success) {
        setDocuments(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  const loadEvaluations = async () => {
    try {
      const response = await axios.get(`/employees/${employeeId}/evaluations`);
      if (response.data.success) {
        setEvaluations(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading evaluations:', error);
      setEvaluations([]);
    }
  };

  const loadActionPlans = async () => {
    try {
      const response = await axios.get(`/employees/${employeeId}/action-plans`);
      if (response.data.success) {
        setActionPlans(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading action plans:', error);
      setActionPlans([]);
    }
  };

  const loadOrganizationData = async () => {
    if (!empresaId) return;
    try {
      const [areasRes, levelsRes, positionsRes, branchesRes, employeesRes] = await Promise.all([
        axios.get(`/organization/areas/${empresaId}`).catch(() => ({ data: { success: false } })),
        axios.get(`/organization/niveles/${empresaId}`).catch(() => ({ data: { success: false } })),
        axios.get(`/organization/puestos/${empresaId}`).catch(() => ({ data: { success: false } })),
        axios.get(`/organization/sucursales/${empresaId}`).catch(() => ({ data: { success: false } })),
        axios.get(`/v5/nom035/${empresaId}/users`).catch(() => ({ data: { success: false } })),
      ]);
      
      if (areasRes.data.success) setAreas(areasRes.data.data || []);
      if (levelsRes.data.success) setLevels(levelsRes.data.data || []);
      if (positionsRes.data.success) setPositions(positionsRes.data.data || []);
      if (branchesRes.data.success) setBranches(branchesRes.data.data || []);
      if (employeesRes.data.success) {
        const users = employeesRes.data.data?.users || employeesRes.data.users || [];
        setEmployees(users.filter(e => e.id !== employeeId)); // Exclude self
      }
    } catch (error) {
      logger.error('Error loading organization data:', error);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.nombre || !formData.apellido_paterno) {
      toast.error(texts.nameRequired);
      return;
    }
    
    setSaving(true);
    try {
      // Admin-only fields that employees cannot edit
      const ADMIN_ONLY_KEYS = new Set([
        'puesto_id', 'area_id', 'nivel_id', 'sucursal_id',
        'jefe_directo_id', 'tipo_contrato_id', 'jornada_id',
        'salario', 'fecha_ingreso', 'fecha_baja',
        'numero_empleado', 'estado_empleado'
      ]);
      
      const orig = originalFormDataRef.current;
      
      // Build delta: only include fields that actually changed
      const cleanData = {};
      Object.keys(formData).forEach(key => {
        // Skip admin-only fields when employee is editing their own profile
        if (!canEditLaboral && ADMIN_ONLY_KEYS.has(key)) return;
        
        const value = formData[key];
        const origValue = orig[key];
        
        // For objects (direccion, contacto_emergencia), compare JSON
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          const cleanedObj = {};
          let hasChange = false;
          const origObj = (typeof origValue === 'object' && origValue) ? origValue : {};
          Object.keys(value).forEach(k => {
            if (value[k] !== '' && value[k] !== undefined && value[k] !== null) {
              cleanedObj[k] = value[k];
              if (value[k] !== origObj[k]) hasChange = true;
            }
          });
          if (hasChange && Object.keys(cleanedObj).length > 0) {
            cleanData[key] = cleanedObj;
          }
        } else {
          // Only send if value changed from original
          if (value !== origValue && value !== '' && value !== undefined && value !== null) {
            cleanData[key] = value;
          }
        }
      });
      
      // Always include nombre and apellido_paterno for backend identification
      if (!cleanData.nombre) cleanData.nombre = formData.nombre;
      if (!cleanData.apellido_paterno) cleanData.apellido_paterno = formData.apellido_paterno;
      
      const response = await axios.put(`/employees/${employeeId}/profile`, cleanData);
      if (response.data.success) {
        toast.success(texts.saved);
        setIsEditing(false);
        loadProfile();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      logger.error('Error saving:', error);
      // Handle error message properly - it might be an object or string
      let errorMsg = texts.error;
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type and size
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(language === 'es' ? 'Solo se aceptan JPG y PNG' : 'Only JPG and PNG accepted');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(language === 'es' ? 'Máximo 2MB' : 'Max 2MB');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `/employees/${employeeId}/photo`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.success) {
        toast.success(texts.photoUpdated);
        loadProfile();
      }
    } catch (error) {
      logger.error('Error uploading photo:', error);
      toast.error(texts.error);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadData.tipo) {
      toast.error(language === 'es' ? 'Selecciona un archivo y tipo' : 'Select a file and type');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', uploadFile);
    formDataUpload.append('tipo', uploadData.tipo);
    if (uploadData.fecha_expiracion) {
      formDataUpload.append('fecha_expiracion', uploadData.fecha_expiracion);
    }
    if (uploadData.notas) {
      formDataUpload.append('notas', uploadData.notas);
    }
    
    try {
      const response = await axios.post(
        `/employees/${employeeId}/documents`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        toast.success(texts.uploadSuccess);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadData({ tipo: '', fecha_expiracion: '', notas: '' });
        loadDocuments();
      }
    } catch (error) {
      logger.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || texts.error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    if (!option) return null;
    return (
      <Badge className={`${option.color} text-white`}>
        {language === 'es' ? option.labelEs : option.labelEn}
      </Badge>
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const selectedDocType = DOCUMENT_TYPES.find(d => d.value === uploadData.tipo);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-teal-600" />
            {texts.title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - Fixed */}
            <div className="w-72 border-r bg-gray-50 p-4 flex flex-col gap-4 overflow-y-auto">
              {/* Profile Photo */}
              <div className="text-center">
                <div className="relative inline-block">
                  <Avatar className="h-24 w-24 mx-auto border-4 border-white shadow-lg">
                    <AvatarImage src={profile?.foto_url} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-2xl">
                      {getInitials(profile?.nombre)}
                    </AvatarFallback>
                  </Avatar>
                  {canEditPhoto && (
                    <label className="absolute bottom-0 right-0 p-1.5 bg-teal-600 rounded-full cursor-pointer hover:bg-teal-700 transition-colors">
                      <Camera className="h-4 w-4 text-white" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                
                <h3 className="mt-3 font-semibold text-lg">
                  {profile?.nombre} {profile?.apellido_paterno}
                </h3>
                <p className="text-sm text-gray-500">
                  {profile?.numero_empleado || formData.numero_empleado || '-'}
                </p>
              </div>
              
              {/* Quick Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{profile?.puesto_nombre || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{profile?.area_nombre || '-'}</span>
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="flex justify-center">
                {getStatusBadge(formData.estado_empleado || 'activo')}
              </div>
              
              {/* MEVIA Score */}
              <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                <CardContent className="py-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Star className="h-4 w-4" />
                    <span className="text-sm font-medium">{texts.meviaScore}</span>
                  </div>
                  {profile?.mevia_score ? (
                    <p className="text-3xl font-bold">{profile.mevia_score}</p>
                  ) : (
                    <p className="text-sm opacity-75">{texts.noScore}</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Quick Actions */}
              <div className="space-y-2 mt-auto">
                {canEditPersonal && !isEditing && (
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    {texts.edit}
                  </Button>
                )}
                {mode !== 'self' && (
                  <Button variant="outline" className="w-full" size="sm">
                    <ClipboardList className="h-4 w-4 mr-2" />
                    {texts.assignEvaluation}
                  </Button>
                )}
                {mode !== 'self' && (
                  <Button variant="outline" className="w-full" size="sm">
                    <FileText className="h-4 w-4 mr-2" />
                    {texts.viewReports}
                  </Button>
                )}
              </div>
            </div>

            {/* Main Content - Tabs */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                <TabsList className={`mx-4 mt-4 grid ${mode === 'self' ? 'grid-cols-4' : 'grid-cols-6'}`}>
                  <TabsTrigger value="personal" className="text-xs">
                    <User className="h-4 w-4 mr-1" />
                    {texts.personal}
                  </TabsTrigger>
                  <TabsTrigger value="address" className="text-xs">
                    <MapPin className="h-4 w-4 mr-1" />
                    {texts.address}
                  </TabsTrigger>
                  <TabsTrigger value="job" className="text-xs">
                    <Briefcase className="h-4 w-4 mr-1" />
                    {texts.job}
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="text-xs">
                    <FileText className="h-4 w-4 mr-1" />
                    {texts.documents}
                  </TabsTrigger>
                  {mode !== 'self' && (
                    <TabsTrigger value="evaluations" className="text-xs">
                      <ClipboardList className="h-4 w-4 mr-1" />
                      {texts.evaluations}
                    </TabsTrigger>
                  )}
                  {mode !== 'self' && (
                    <TabsTrigger value="actionPlans" className="text-xs">
                      <Target className="h-4 w-4 mr-1" />
                      {texts.actionPlans}
                    </TabsTrigger>
                  )}
                </TabsList>

                <div className="flex-1 overflow-y-auto p-4">
                  {/* Save/Cancel buttons for editable tabs */}
                  {isEditing && (activeTab === 'personal' || activeTab === 'address' || activeTab === 'job') && (
                    <div className="flex justify-end gap-2 mb-4">
                      <Button size="sm" onClick={handleSave} disabled={saving}>
                        <Save className="h-4 w-4 mr-1" />
                        {texts.save}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                        <X className="h-4 w-4 mr-1" />
                        {texts.cancel}
                      </Button>
                    </div>
                  )}

                  {/* Personal Tab */}
                  <TabsContent value="personal" className="mt-0 space-y-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.firstName} *</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.nombre || ''}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{profile?.nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.lastName} *</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.apellido_paterno || ''}
                                onChange={(e) => setFormData({ ...formData, apellido_paterno: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{profile?.apellido_paterno || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.motherLastName}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.apellido_materno || ''}
                                onChange={(e) => setFormData({ ...formData, apellido_materno: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{profile?.apellido_materno || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.birthDate}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                type="date"
                                value={formData.fecha_nacimiento || ''}
                                onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formatDate(profile?.fecha_nacimiento)}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.gender}</label>
                            {isEditing && canEditPersonal ? (
                              <Select
                                value={formData.genero || ''}
                                onValueChange={(v) => setFormData({ ...formData, genero: v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  {GENDER_OPTIONS.map(g => (
                                    <SelectItem key={g.value} value={g.value}>
                                      {language === 'es' ? g.labelEs : g.labelEn}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 font-medium">
                                {GENDER_OPTIONS.find(g => g.value === profile?.genero)?.[language === 'es' ? 'labelEs' : 'labelEn'] || '-'}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.nationality}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.nacionalidad || ''}
                                onChange={(e) => setFormData({ ...formData, nacionalidad: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{profile?.nacionalidad || '-'}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.curp}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.curp || ''}
                                onChange={(e) => setFormData({ ...formData, curp: e.target.value.toUpperCase() })}
                                maxLength={18}
                                className="mt-1 font-mono"
                              />
                            ) : (
                              <p className="mt-1 font-mono">{profile?.curp || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.rfc}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.rfc || ''}
                                onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                                maxLength={13}
                                className="mt-1 font-mono"
                              />
                            ) : (
                              <p className="mt-1 font-mono">{profile?.rfc || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.nss}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.nss || ''}
                                onChange={(e) => setFormData({ ...formData, nss: e.target.value.replace(/\D/g, '') })}
                                maxLength={11}
                                className="mt-1 font-mono"
                              />
                            ) : (
                              <p className="mt-1 font-mono">{profile?.nss || '-'}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.phone}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.telefono || ''}
                                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{profile?.telefono || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.email}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                type="email"
                                value={formData.email || ''}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{profile?.email || '-'}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Address Tab */}
                  <TabsContent value="address" className="mt-0 space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">{texts.address}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-500">{texts.street}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.calle || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, calle: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.calle || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.extNumber}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.numero_exterior || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, numero_exterior: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.numero_exterior || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.intNumber}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.numero_interior || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, numero_interior: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.numero_interior || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.neighborhood}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.colonia || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, colonia: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.colonia || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.zipCode}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.codigo_postal || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, codigo_postal: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.codigo_postal || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.city}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.ciudad || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, ciudad: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.ciudad || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.state}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.direccion?.estado_codigo || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  direccion: { ...formData.direccion, estado_codigo: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.direccion?.estado_codigo || '-'}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Emergency Contact */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {texts.emergencyContact}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.contactName}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.contacto_emergencia?.nombre || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  contacto_emergencia: { ...formData.contacto_emergencia, nombre: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.contacto_emergencia?.nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.relationship}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.contacto_emergencia?.parentesco || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  contacto_emergencia: { ...formData.contacto_emergencia, parentesco: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.contacto_emergencia?.parentesco || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.contactPhone}</label>
                            {isEditing && canEditPersonal ? (
                              <Input
                                value={formData.contacto_emergencia?.telefono || ''}
                                onChange={(e) => setFormData({ 
                                  ...formData, 
                                  contacto_emergencia: { ...formData.contacto_emergencia, telefono: e.target.value }
                                })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.contacto_emergencia?.telefono || '-'}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Job Tab */}
                  <TabsContent value="job" className="mt-0 space-y-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.employeeNumber} *</label>
                            {isEditing && canEditLaboral ? (
                              <Input
                                value={formData.numero_empleado || ''}
                                onChange={(e) => setFormData({ ...formData, numero_empleado: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formData.numero_empleado || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.hireDate} *</label>
                            {isEditing && canEditLaboral ? (
                              <Input
                                type="date"
                                value={formData.fecha_ingreso || ''}
                                onChange={(e) => setFormData({ ...formData, fecha_ingreso: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formatDate(formData.fecha_ingreso)}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.terminationDate}</label>
                            {isEditing && canEditLaboral ? (
                              <Input
                                type="date"
                                value={formData.fecha_baja || ''}
                                onChange={(e) => setFormData({ ...formData, fecha_baja: e.target.value })}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{formatDate(formData.fecha_baja) || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.area}</label>
                            {isEditing && canEditLaboral ? (
                              <Select
                                value={formData.area_id || '_none'}
                                onValueChange={(v) => setFormData({ ...formData, area_id: v === '_none' ? '' : v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">-</SelectItem>
                                  {areas.map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 font-medium">{profile?.area_nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.position}</label>
                            {isEditing && canEditLaboral ? (
                              <Select
                                value={formData.puesto_id || '_none'}
                                onValueChange={(v) => setFormData({ ...formData, puesto_id: v === '_none' ? '' : v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">-</SelectItem>
                                  {positions.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 font-medium">{profile?.puesto_nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.level}</label>
                            {isEditing && canEditLaboral ? (
                              <Select
                                value={formData.nivel_id || '_none'}
                                onValueChange={(v) => setFormData({ ...formData, nivel_id: v === '_none' ? '' : v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">-</SelectItem>
                                  {levels.map(l => (
                                    <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 font-medium">{profile?.nivel_nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.directBoss}</label>
                            {isEditing && canEditLaboral ? (
                              <Select
                                value={formData.jefe_directo_id || '_none'}
                                onValueChange={(v) => setFormData({ ...formData, jefe_directo_id: v === '_none' ? '' : v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">-</SelectItem>
                                  {employees.map(e => (
                                    <SelectItem key={e.id} value={e.id}>{e.nombre || e.nombre_completo}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 font-medium">{profile?.jefe_nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.branch}</label>
                            {isEditing && canEditLaboral ? (
                              <Select
                                value={formData.sucursal_id || '_none'}
                                onValueChange={(v) => setFormData({ ...formData, sucursal_id: v === '_none' ? '' : v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">-</SelectItem>
                                  {branches.map(b => (
                                    <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 font-medium">{profile?.sucursal_nombre || '-'}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">{texts.employeeStatus}</label>
                            {isEditing && canEditLaboral ? (
                              <Select
                                value={formData.estado_empleado || 'activo'}
                                onValueChange={(v) => setFormData({ ...formData, estado_empleado: v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map(s => (
                                    <SelectItem key={s.value} value={s.value}>
                                      {language === 'es' ? s.labelEs : s.labelEn}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="mt-1">{getStatusBadge(formData.estado_empleado)}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Salary - only visible to admin */}
                        {canViewSalary && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="max-w-xs">
                              <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                                <DollarSign className="h-4 w-4" />
                                {texts.salary}
                              </label>
                              {isEditing && canEditLaboral ? (
                                <Input
                                  type="number"
                                  value={formData.salario || ''}
                                  onChange={(e) => setFormData({ ...formData, salario: e.target.value })}
                                  className="mt-1"
                                  placeholder="0.00"
                                />
                              ) : (
                                <p className="mt-1 font-medium">
                                  {formData.salario ? `$${Number(formData.salario).toLocaleString()}` : '-'}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Documents Tab */}
                  <TabsContent value="documents" className="mt-0 space-y-4">
                    <div className="flex justify-end">
                      <Button size="sm" onClick={() => setUploadDialogOpen(true)}>
                        <Upload className="h-4 w-4 mr-1" />
                        {texts.uploadDocument}
                      </Button>
                    </div>

                    {documents.length === 0 ? (
                      <Card>
                        <CardContent className="py-12 text-center text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p>{texts.noDocuments}</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((doc) => {
                          const isExpired = doc.fecha_expiracion && new Date(doc.fecha_expiracion) < new Date();
                          return (
                            <Card key={doc.id} className={isExpired ? 'border-red-200 bg-red-50' : ''}>
                              <CardContent className="py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <FileText className={`h-8 w-8 ${isExpired ? 'text-red-400' : 'text-gray-400'}`} />
                                  <div>
                                    <p className="font-medium text-sm">{doc.nombre_archivo}</p>
                                    <p className="text-xs text-gray-500">
                                      {doc.tipo_documento_nombre || doc.tipo}
                                      {doc.fecha_expiracion && (
                                        <span className={`ml-2 ${isExpired ? 'text-red-500 font-medium' : ''}`}>
                                          {isExpired ? texts.expired : texts.expiresOn}: {formatDate(doc.fecha_expiracion)}
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      {texts.uploadDate}: {formatDate(doc.created_at)}
                                      {doc.uploaded_by_name && ` • ${texts.uploadedBy}: ${doc.uploaded_by_name}`}
                                    </p>
                                  </div>
                                </div>
                                {isExpired && (
                                  <Badge variant="destructive">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {texts.expired}
                                  </Badge>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>

                  {/* Evaluations Tab - Read Only */}
                  <TabsContent value="evaluations" className="mt-0 space-y-4">
                    {evaluations.length === 0 ? (
                      <Card>
                        <CardContent className="py-12 text-center text-gray-500">
                          <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p>{texts.noEvaluations}</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-2">
                        {evaluations.map((ev) => (
                          <Card key={ev.id}>
                            <CardContent className="py-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-teal-100 rounded-full">
                                  <Award className="h-5 w-5 text-teal-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{ev.evaluation_name || ev.tipo}</p>
                                  <p className="text-sm text-gray-500">
                                    {texts.evaluationDate}: {formatDate(ev.fecha_completado || ev.created_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {ev.score && (
                                  <Badge variant="outline" className="text-lg px-3 py-1">
                                    {ev.score}
                                  </Badge>
                                )}
                                <Button variant="outline" size="sm">
                                  {texts.viewResults}
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Action Plans Tab - Read Only */}
                  <TabsContent value="actionPlans" className="mt-0 space-y-4">
                    {actionPlans.length === 0 ? (
                      <Card>
                        <CardContent className="py-12 text-center text-gray-500">
                          <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p>{texts.noPlans}</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-2">
                        {actionPlans.map((plan) => (
                          <Card key={plan.id}>
                            <CardContent className="py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-100 rounded-full">
                                    <Target className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{plan.titulo || plan.name}</p>
                                    <p className="text-sm text-gray-500">
                                      {texts.dueDate}: {formatDate(plan.fecha_limite)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge variant={plan.status === 'completed' ? 'success' : 'outline'}>
                                    {plan.status || texts.planStatus}
                                  </Badge>
                                  {plan.progress !== undefined && (
                                    <span className="text-sm font-medium">{plan.progress}%</span>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </div>
        )}

        {/* Upload Document Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{texts.uploadDocument}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1 block">{texts.documentType} *</label>
                <Select
                  value={uploadData.tipo}
                  onValueChange={(v) => setUploadData({ ...uploadData, tipo: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="-" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {language === 'es' ? t.labelEs : t.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {language === 'es' ? 'Archivo' : 'File'} *
                </label>
                <Input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
              </div>
              {selectedDocType?.hasExpiration && (
                <div>
                  <label className="text-sm font-medium mb-1 block">{texts.expirationDate}</label>
                  <Input
                    type="date"
                    value={uploadData.fecha_expiracion}
                    onChange={(e) => setUploadData({ ...uploadData, fecha_expiracion: e.target.value })}
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {language === 'es' ? 'Notas' : 'Notes'}
                </label>
                <Textarea
                  value={uploadData.notas}
                  onChange={(e) => setUploadData({ ...uploadData, notas: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                {texts.cancel}
              </Button>
              <Button onClick={handleUploadDocument}>
                <Upload className="h-4 w-4 mr-1" />
                {texts.upload}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeExpediente;
